// interview-required-answers-gate.ts
//
// "Người đã trả lời chưa" — phần gate của interview, tách khỏi
// interview-store.ts (201 dòng, quá ngân sách 200). Store giữ việc đọc/ghi
// câu hỏi và câu trả lời; file này giữ luật câu nào chặn được dispatch nào.

import { readInterview } from "./interview-store.js";
import { needsDecisionQuestionTicket } from "./needs-decision-question-ticket-match.js";
import type { InterviewQuestion } from "./schemas/interview-schema.js";

/**
 * Câu hỏi này có thuộc về riêng một ticket khác không?
 *
 * Chỉ câu `needs_decision` (nguyên id `<ticket>-needs-decision`
 * có/không `-<sha8>`) mới gắn với một ticket. Câu viết tay ở bước Clarify (id
 * tự do: `q-env`, `q-budget`) là câu của CẢ MISSION nên không bao giờ bị lọc bỏ.
 */
function belongsToAnotherTicket(
  question: InterviewQuestion,
  ticketId: string | undefined,
): boolean {
  const owner = needsDecisionQuestionTicket(question);
  if (owner === undefined || ticketId === undefined) return false;
  return owner !== ticketId;
}

/**
 * Id của các câu `required` CHƯA được trả lời — rỗng nghĩa là không chặn gì.
 *
 * Fail-OPEN có chủ ý, ngược với gate authority (`feature-dispatch-gate-guard.ts`
 * fail-CLOSED): mission không có interview.json là trạng thái BÌNH THƯỜNG của
 * gần như mọi mission hôm nay (gồm 6 ticket fixture e2e). Chặn khi thiếu file
 * sẽ dừng mọi mission chưa từng hỏi câu nào — hỏng nặng hơn nhiều so với rủi
 * ro bỏ sót. Thiếu quyền là chuyện của G0/G1; đây chỉ là "người đã trả lời chưa".
 *
 * `ticketId` (H1, code-reviewer 260916 phase-06) — bộ LỌC tuỳ chọn, không phải
 * điều kiện hoạt động:
 *  - có ticket ⇒ bỏ qua câu `needs-decision` của ticket KHÁC. Trước bản vá này
 *    một câu sinh từ T-0007 chặn luôn T-0008/T-0009 không liên quan, trái hẳn
 *    điều `escalation.md` hứa với người vận hành ("ticket độc lập vẫn chạy");
 *  - KHÔNG có ticket ⇒ trả về mọi câu required, giữ nguyên hợp đồng cũ cho mọi
 *    call site một tham số.
 *
 * Câu viết tay bước Clarify vẫn chặn TOÀN mission ở cả hai chế độ — chúng là
 * câu về môi trường/ngân sách/PII của cả mission, không của riêng ticket nào.
 */
export async function requireAnsweredQuestionIds(
  controlRoot: string,
  ticketId?: string,
): Promise<string[]> {
  const interview = await readInterview(controlRoot);
  if (interview === undefined) return [];
  return interview.questions
    .filter((q) => q.required && interview.answers[q.id] === undefined)
    .filter((q) => !belongsToAnotherTicket(q, ticketId))
    .map((q) => q.id);
}
